import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../context/ThemeContext';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../../../utils/api';
import { UserPlus, Search, ToggleLeft, ToggleRight, Trash2, X, Mail, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';

const ClientUsersPanel = ({ clientId }) => {
    const { theme } = useTheme();
    const dark = theme === 'dark';
    const cardBg = dark ? 'bg-gray-800' : 'bg-white';
    const borderColor = dark ? 'border-gray-700' : 'border-gray-200';

    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);

    // Roles
    const [roles, setRoles] = useState([]);

    // Invitations
    const [invitations, setInvitations] = useState([]);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '', client_role_id: '' });
    const [inviting, setInviting] = useState(false);

    useEffect(() => { fetchUsers(); fetchInvitations(); fetchRoles(); }, [page, search, clientId]);

    const cq = clientId ? `&client_id=${clientId}` : '';
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, ...(search && { search }) });
            const res = await apiGet(`/client_manager/users?${params}${cq}`);
            if (res.ok) {
                const data = await res.json();
                setUsers(data.users);
                setPagination(data.pagination);
            }
        } catch {} finally { setLoading(false); }
    };

    const fetchInvitations = async () => {
        try {
            const res = await apiGet(`/client_manager/invitations?${clientId ? `client_id=${clientId}` : ''}`);
            if (res.ok) {
                const data = await res.json();
                setInvitations(data.invitations);
            }
        } catch {}
    };

    const fetchRoles = async () => {
        try {
            const res = await apiGet(`/client_manager/roles?${clientId ? `client_id=${clientId}` : ''}`);
            if (res.ok) {
                const data = await res.json();
                setRoles(data.roles);
            }
        } catch {}
    };

    const handleAssignRole = async (membershipId, roleId) => {
        try {
            const res = await apiPatch(`/client_manager/users/${membershipId}/assign_role`, {
                client_role_id: roleId || null
            });
            if (res.ok) {
                toast.success('Role updated');
                fetchUsers();
            } else {
                const data = await res.json();
                toast.error(data.error || 'Failed to update');
            }
        } catch { toast.error('Failed to update'); }
    };

    const handleInvite = async () => {
        if (!inviteForm.email.trim()) { toast.error('Email is required'); return; }
        setInviting(true);
        try {
            const res = await apiPost('/client_manager/invitations', inviteForm);
            if (res.ok) {
                const data = await res.json();
                toast.success(data.message);
                setShowInviteModal(false);
                setInviteForm({ email: '', first_name: '', last_name: '', client_role_id: '' });
                fetchInvitations();
            } else {
                const data = await res.json();
                toast.error(data.error || 'Failed to send invitation');
            }
        } catch { toast.error('Failed to send invitation'); }
        finally { setInviting(false); }
    };

    const handleRevokeInvitation = async (id) => {
        try {
            const res = await apiDelete(`/client_manager/invitations/${id}`);
            if (res.ok) {
                toast.success('Invitation revoked');
                fetchInvitations();
            }
        } catch { toast.error('Failed to revoke'); }
    };

    const handleToggleActive = async (membership) => {
        try {
            const res = await apiPatch(`/client_manager/users/${membership.id}/toggle_active`);
            if (res.ok) {
                const data = await res.json();
                toast.success(data.message);
                fetchUsers();
            }
        } catch { toast.error('Failed to update'); }
    };

    const handleRemove = async (membership) => {
        if (!confirm(`Remove ${membership.first_name || membership.username} from the organization?`)) return;
        try {
            const res = await apiDelete(`/client_manager/users/${membership.id}`);
            if (res.ok) {
                toast.success('User removed');
                fetchUsers();
            } else {
                const data = await res.json();
                toast.error(data.error || 'Failed to remove');
            }
        } catch { toast.error('Failed to remove'); }
    };

    const pendingInvites = invitations.filter(i => i.status === 'pending');

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-dm-serif">Team Members</h2>
                <button onClick={() => setShowInviteModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-rasa hover:bg-blue-600">
                    <UserPlus size={16} /> Invite User
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    placeholder="Search users..."
                    className={`w-full pl-9 pr-4 py-2 rounded-lg border ${borderColor} ${dark ? 'bg-gray-800' : 'bg-white'} font-rasa text-sm`}
                />
            </div>

            {/* Pending Invitations */}
            {pendingInvites.length > 0 && (
                <div className={`p-4 rounded-lg border ${borderColor} ${cardBg}`}>
                    <h3 className="font-dm-serif text-sm mb-3 flex items-center gap-2"><Mail size={14} /> Pending Invitations ({pendingInvites.length})</h3>
                    <div className="space-y-2">
                        {pendingInvites.map(inv => (
                            <div key={inv.id} className={`flex items-center justify-between p-2 rounded text-sm font-rasa ${dark ? 'bg-gray-750' : 'bg-orange-50'}`}>
                                <div>
                                    <span className="font-medium">{inv.email}</span>
                                    {inv.first_name && <span className="text-gray-500 ml-2">({inv.first_name} {inv.last_name})</span>}
                                    <span className="text-xs text-gray-400 ml-2 flex items-center gap-1 inline-flex">
                                        <Clock size={10} /> Expires {new Date(inv.expires_at).toLocaleDateString()}
                                    </span>
                                </div>
                                <button onClick={() => handleRevokeInvitation(inv.id)} className="text-red-500 hover:text-red-600 p-1" title="Revoke">
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Users Table */}
            {loading ? (
                <div className="text-center py-8 text-gray-500 font-rasa">Loading...</div>
            ) : users.length === 0 ? (
                <div className={`text-center py-8 rounded-lg ${cardBg} border ${borderColor}`}>
                    <p className="text-gray-500 font-rasa">No team members yet. Invite users to get started.</p>
                </div>
            ) : (
                <div className={`rounded-lg border ${borderColor} overflow-hidden`}>
                    <table className="w-full text-sm font-rasa">
                        <thead className={dark ? 'bg-gray-750' : 'bg-gray-50'}>
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">User</th>
                                <th className="text-left px-4 py-3 font-medium">Email</th>
                                <th className="text-left px-4 py-3 font-medium">Role</th>
                                <th className="text-left px-4 py-3 font-medium">Status</th>
                                <th className="text-left px-4 py-3 font-medium">Joined</th>
                                <th className="text-right px-4 py-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody className={cardBg}>
                            {users.map(u => (
                                <tr key={u.id} className={`border-t ${borderColor}`}>
                                    <td className="px-4 py-3">
                                        <span className="font-medium">{u.first_name} {u.last_name}</span>
                                        <span className="text-gray-500 text-xs ml-1">@{u.username}</span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">{u.email}</td>
                                    <td className="px-4 py-3">
                                        <select
                                            value={u.client_role_id || ''}
                                            onChange={e => handleAssignRole(u.id, e.target.value)}
                                            className={`text-xs font-rasa px-2 py-1 rounded border ${borderColor} ${dark ? 'bg-gray-700' : 'bg-white'}`}
                                        >
                                            <option value="">No Role</option>
                                            {roles.map(r => (
                                                <option key={r.id} value={r.id}>{r.name}</option>
                                            ))}
                                        </select>
                                        {u.client_role_name && (
                                            <span className="ml-2 px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: u.client_role_color || '#6B7280' }}>
                                                {u.client_role_name}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${u.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {u.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">{u.joined_at ? new Date(u.joined_at).toLocaleDateString() : '—'}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => handleToggleActive(u)} className={`p-1 rounded ${dark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} title={u.active ? 'Deactivate' : 'Activate'}>
                                                {u.active ? <ToggleRight size={18} className="text-green-500" /> : <ToggleLeft size={18} className="text-gray-400" />}
                                            </button>
                                            <button onClick={() => handleRemove(u)} className="p-1 rounded text-red-500 hover:bg-red-50" title="Remove">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
                <div className="flex items-center justify-center gap-4 text-sm font-rasa">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1 disabled:opacity-30"><ChevronLeft size={18} /></button>
                    <span>Page {pagination.current_page} of {pagination.total_pages}</span>
                    <button onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))} disabled={page === pagination.total_pages} className="p-1 disabled:opacity-30"><ChevronRight size={18} /></button>
                </div>
            )}

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowInviteModal(false)}>
                    <div className={`p-6 rounded-lg w-96 ${cardBg}`} onClick={e => e.stopPropagation()}>
                        <h3 className="font-dm-serif mb-4">Invite Team Member</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-rasa text-gray-500 mb-1">Email *</label>
                                <input
                                    type="email"
                                    value={inviteForm.email}
                                    onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                                    className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${dark ? 'bg-gray-700' : 'bg-white'} font-rasa text-sm`}
                                    placeholder="user@example.com"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-sm font-rasa text-gray-500 mb-1">First Name</label>
                                    <input
                                        type="text"
                                        value={inviteForm.first_name}
                                        onChange={e => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                                        className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${dark ? 'bg-gray-700' : 'bg-white'} font-rasa text-sm`}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-rasa text-gray-500 mb-1">Last Name</label>
                                    <input
                                        type="text"
                                        value={inviteForm.last_name}
                                        onChange={e => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                                        className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${dark ? 'bg-gray-700' : 'bg-white'} font-rasa text-sm`}
                                    />
                                </div>
                            </div>
                            {roles.length > 0 && (
                                <div>
                                    <label className="block text-sm font-rasa text-gray-500 mb-1">Role</label>
                                    <select
                                        value={inviteForm.client_role_id}
                                        onChange={e => setInviteForm({ ...inviteForm, client_role_id: e.target.value })}
                                        className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${dark ? 'bg-gray-700' : 'bg-white'} font-rasa text-sm`}
                                    >
                                        <option value="">No Role</option>
                                        {roles.map(r => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 font-rasa mt-3">
                            An invitation email will be sent. If they don't have an account, they'll be prompted to create one.
                        </p>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setShowInviteModal(false)} className={`px-4 py-2 rounded-lg text-sm font-rasa ${dark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>Cancel</button>
                            <button onClick={handleInvite} disabled={inviting} className="px-4 py-2 rounded-lg text-sm font-rasa bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50">
                                {inviting ? 'Sending...' : 'Send Invitation'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientUsersPanel;
